import React, { useState, useRef } from 'react';
import { Play, Pause } from 'lucide-react';

const VideoBanner = ({ settings }) => {
    const {
        title = "Watch Our Story",
        subheading = "See how our products are made",
        videoUrl = "https://www.w3schools.com/html/mov_bbb.mp4",
        height = "medium", // small, medium, large, custom
        customHeightDesktop = "500px",
        customHeightTablet = "400px",
        customHeightMobile = "300px",
        width = "full", // full, wide, medium, small, custom
        customWidthDesktop = "100%",
        customWidthTablet = "100%",
        customWidthMobile = "100%",
        overlayOpacity = 0.5
    } = settings;

    const [isPlaying, setIsPlaying] = useState(true);
    const [isMuted, setIsMuted] = useState(true);
    const [showOverlay, setShowOverlay] = useState(true);
    const videoRef = useRef(null);

    let heightStyle = {};
    let heightClassName = "";

    if (height === 'custom') {
        heightStyle = {
            '--desktop-height': customHeightDesktop,
            '--tablet-height': customHeightTablet,
            '--mobile-height': customHeightMobile
        };
        heightClassName = "h-[var(--mobile-height)] md:h-[var(--tablet-height)] lg:h-[var(--desktop-height)]";
    } else {
        heightClassName = {
            small: "h-[300px]",
            medium: "h-[500px]",
            large: "h-[700px]"
        }[height] || "h-[500px]";
    }

    let containerStyle = { ...heightStyle };
    let containerClass = `relative overflow-hidden bg-black mx-auto ${heightClassName}`;

    if (width === 'custom') {
        containerStyle = { 
            ...containerStyle, 
            '--desktop-width': customWidthDesktop,
            '--tablet-width': customWidthTablet,
            '--mobile-width': customWidthMobile,
            maxWidth: 'var(--mobile-width)'
        };
        // We need to use a style tag or inline styles for responsive max-width because tailwind arbitrary values don't support CSS variables in this specific way easily for responsive variants without configuration
        // Actually, we can use the style attribute for the variable values and classes for the breakpoints if we set the variable on the element.
        // But max-width is tricky with variables responsively in inline styles.
        // Let's use a class that sets max-width to the variable, and change the variable value at breakpoints? No, variables cascade.
        // Better approach: Use the style prop to set the variables, and use arbitrary tailwind classes that reference those variables.
        // max-w-[var(--mobile-width)] md:max-w-[var(--tablet-width)] lg:max-w-[var(--desktop-width)]
        
        containerClass += " w-full max-w-[var(--mobile-width)] md:max-w-[var(--tablet-width)] lg:max-w-[var(--desktop-width)]";
        
        // Clean up the direct maxWidth assignment from containerStyle since we are using classes now
        delete containerStyle.maxWidth;
    } else {
        const widthClasses = {
            full: "w-full",
            wide: "w-full max-w-[1280px]",
            medium: "w-full max-w-[1024px]",
            small: "w-full max-w-[600px]"
        };
        containerClass += ` ${widthClasses[width] || "w-full"}`;
    }

    const getYoutubeId = (url) => {
        if (!url) return null;
        const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
        const match = url.match(regExp);
        return (match && match[2].length === 11) ? match[2] : null;
    };

    const youtubeId = getYoutubeId(videoUrl);

    const togglePlay = () => {
        if (youtubeId) {
            setShowOverlay(false);
            return;
        }

        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause();
            } else {
                videoRef.current.play();
            }
            setIsPlaying(!isPlaying);
        }
    };

    const toggleMute = () => {
        if (videoRef.current) {
            videoRef.current.muted = !isMuted;
            setIsMuted(!isMuted);
        }
    };

    return (
        <div 
            className={`${containerClass} group`}
            style={containerStyle}
        >
            {/* Video Background */}
            {youtubeId ? (
                <iframe
                    className={`absolute ${showOverlay ? 'top-1/2 left-1/2 w-[300%] h-[300%] -translate-x-1/2 -translate-y-1/2' : 'top-0 left-0 w-full h-full'} object-cover ${showOverlay ? 'pointer-events-none' : 'pointer-events-auto'}`}
                    src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1&mute=1&loop=1&playlist=${youtubeId}&controls=${showOverlay ? 0 : 1}&showinfo=0&rel=0`}
                    allow="autoplay; encrypted-media"
                    title="Video Banner"
                />
            ) : (
                videoUrl && (
                    <video
                        ref={videoRef}
                        className="absolute top-0 left-0 w-full h-full object-cover"
                        src={videoUrl}
                        autoPlay
                        loop
                        muted
                        playsInline
                    />
                )
            )}
            
            {/* Overlay */}
            {showOverlay && (
                <div 
                    className="absolute top-0 left-0 w-full h-full flex flex-col items-center justify-center text-center px-4 z-10 transition-all duration-300"
                    style={{ backgroundColor: `rgba(0,0,0,${overlayOpacity})` }}
                >
                    {title && (
                        <h2 className="text-5xl md:text-4xl font-bold text-white mb-4 tracking-tight drop-shadow-lg">
                            {title}
                        </h2>
                    )}
                    {subheading && (
                        <p className="text-xl md:text-lg text-gray-100 max-w-2xl drop-shadow-md mb-8">
                            {subheading}
                        </p>
                    )}

                    {/* Play/Pause Button */}
                    <button
                        onClick={togglePlay}
                        className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-md border border-white/30 flex items-center justify-center text-white transition-all duration-300 hover:bg-white/30 hover:scale-105 active:scale-95"
                    >
                        {youtubeId ? (
                            <Play size={24} fill="currentColor" className="ml-1 opacity-90" />
                        ) : isPlaying ? (
                            <Pause size={24} fill="currentColor" className="opacity-90" />
                        ) : (
                            <Play size={24} fill="currentColor" className="ml-1 opacity-90" />
                        )}
                    </button>
                </div>
            )}
        </div>
    );
};

export default VideoBanner;
